import { useEffect, useState, useCallback } from "react";
import { Loader2, UserCheck, Mail, Clock, CreditCard, Activity } from "lucide-react";
import api from "@/lib/api";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");

export default function AccountActivity({ athleteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/athletes/${athleteId}/account-activity`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12 text-white/40"><Loader2 className="animate-spin" /></div>;
  if (!data) return <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6">Could not load account activity.</div>;

  const rows = [
    { icon: UserCheck, label: "Account Status", value: (data.account_status || "—").toUpperCase() },
    { icon: Mail, label: "Email", value: data.email || "—" },
    { icon: Activity, label: "Email Verified", value: data.activated ? "Yes" : "No" },
    { icon: Clock, label: "Invitation Sent", value: fmt(data.invited_at) },
    { icon: Clock, label: "Activated", value: fmt(data.activated_at) },
    { icon: Clock, label: "Last Login", value: fmt(data.last_login) },
    { icon: Clock, label: "Last Profile Update", value: fmt(data.last_profile_update) },
    { icon: CreditCard, label: "Membership", value: `${data.membership_plan || "None"} (${data.membership_status})` },
  ];

  return (
    <div data-testid="account-activity">
      {!data.linked && (
        <div className="mb-5 border border-white/20 bg-white/5 p-3 text-sm text-white/70" data-testid="account-not-linked">
          No activated login is linked to this athlete yet. It will connect automatically when they activate their invite.
        </div>
      )}
      <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.label} className="flex items-center justify-between px-5 py-3.5" data-testid={`activity-${r.label.toLowerCase().replace(/\s/g, "-")}`}>
              <span className="flex items-center gap-3 text-white/60 text-sm"><Icon size={15} className="text-white/40" /> {r.label}</span>
              <span className="text-sm font-medium text-right">{r.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
